import React, { useContext, useEffect, useMemo, useState, useCallback } from "react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography
} from "@material-ui/core";
import Autocomplete, { createFilterOptions } from "@material-ui/lab/Autocomplete";
import { makeStyles } from "@material-ui/core/styles";
import { toast } from "react-toastify";

import api from "../../services/api";
import { isWhatsappEligibleForCampaigns } from "../../utils/whatsappCampaignEligibility";
import officialCampaignService from "../../services/officialCampaignService";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles(theme => ({
  templateEditor: {
    fontFamily: "monospace",
    minHeight: 200
  },
  sectionTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(1)
  },
  offlineDot: {
    width: 10,
    height: 10,
    borderRadius: "50%",
    display: "inline-block",
    marginRight: theme.spacing(1)
  }
}));

const defaultForm = {
  name: "",
  contactListId: "",
  contactListProId: "",
  tagIds: [],
  whatsappId: "",
  ticketRegister: false,
  ticketStatus: "closed",
  ticketQueueId: "",
  ticketUserId: "",
  scheduledAt: "",
  templateName: "",
  templateLanguage: "",
  templateComponentsJson: "[]",
  sendInteractive: false,
  interactiveType: "button",
  interactiveBodyText: "",
  interactiveButtonText: "Escolha uma opção",
  interactiveButtons: [{ id: "1", title: "" }, { id: "2", title: "" }, { id: "3", title: "" }],
  interactiveSections: [{ title: "Opções", rows: [{ id: "1", title: "" }] }]
};

const OfficialCampaignModal = ({ open, onClose, onSuccess, initialContactListProId, duplicateFromId }) => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);

  const [form, setForm] = useState(defaultForm);
  const [contactLists, setContactLists] = useState([]);
  const [contactListsPro, setContactListsPro] = useState([]);
  const [tags, setTags] = useState([]);
  const [queues, setQueues] = useState([]);
  const [users, setUsers] = useState([]);
  const [whatsapps, setWhatsapps] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [loadingDuplicate, setLoadingDuplicate] = useState(false);

  const companyId = user?.companyId;

  useEffect(() => {
    if (!open || !companyId) {
      // Reset form quando modal fechar
      if (!open) {
        setForm(defaultForm);
        setSelectedTemplate(null);
        setTemplates([]);
      }
      return;
    }

    const fetchData = async () => {
      try {
        const [
          { data: lists },
          { data: listsPro },
          { data: tagList },
          { data: queueData },
          { data: usersData },
          { data: whatsappData }
        ] = await Promise.all([
          api.get("/contact-lists/list", { params: { companyId } }),
          api.get("/contact-lists-pro/list", { params: { companyId } }),
          api.get("/tags/list", { params: { companyId, kanban: 0 } }),
          api.get("/queue"),
          api.get("/users/", { params: { showAll: true } }),
          api.get("/whatsapp", { params: { companyId, session: 0 } })
        ]);

        setContactLists(lists || []);
        setContactListsPro(listsPro || []);
        setTags(tagList || []);
        setQueues(queueData || []);
        setUsers(usersData?.users || []);
        
        const officials = (whatsappData || []).filter(
          w => w.channel === "whatsapp_oficial" && isWhatsappEligibleForCampaigns(w)
        );
        setWhatsapps(officials);
      } catch (err) {
        toastError(err);
      }
    };

    fetchData();
  }, [open, companyId]);

  // Prefill ao duplicar (mesmo padrão da Campanha PRO)
  useEffect(() => {
    if (!open || !duplicateFromId) return;
    let cancelled = false;

    const loadDuplicate = async () => {
      setLoadingDuplicate(true);
      try {
        const { data } = await officialCampaignService.show(duplicateFromId);
        if (cancelled || !data) return;

        const interactive = data.interactivePayload || null;
        const nextForm = {
          ...defaultForm,
          name: `Cópia de ${data.name || ""}`.trim(),
          contactListId: data.contactListId || "",
          contactListProId: data.contactListProId || "",
          tagIds: Array.isArray(data.tagIds) ? data.tagIds : [],
          whatsappId: data.whatsappId || "",
          ticketRegister: !!data.ticketRegister,
          ticketStatus: data.ticketStatus || "closed",
          ticketQueueId: data.ticketQueueId || "",
          ticketUserId: data.ticketUserId || "",
          scheduledAt: "",
          templateName: data.templateName || "",
          templateLanguage: data.templateLanguage || "",
          templateComponentsJson: JSON.stringify(data.templateComponents || [], null, 2),
          sendInteractive: !!interactive,
          interactiveType: interactive?.type === "list" ? "list" : "button",
          interactiveBodyText: interactive?.bodyText || "",
          interactiveButtonText: interactive?.buttonText || "Escolha uma opção",
          interactiveButtons: interactive?.type === "button" && Array.isArray(interactive.buttons)
            ? [
                ...interactive.buttons,
                { id: "1", title: "" },
                { id: "2", title: "" },
                { id: "3", title: "" }
              ].slice(0, 3)
            : defaultForm.interactiveButtons,
          interactiveSections: interactive?.type === "list" && Array.isArray(interactive.sections)
            ? interactive.sections
            : defaultForm.interactiveSections
        };
        setForm(nextForm);

        if (data.whatsappId) {
          const { data: tplData } = await officialCampaignService.templates(data.whatsappId);
          if (cancelled) return;
          const list = tplData?.data || [];
          setTemplates(list);
          const match =
            list.find(
              (t) =>
                t.name === data.templateName &&
                (t.language === data.templateLanguage ||
                  t.language?.code === data.templateLanguage)
            ) ||
            list.find((t) => t.name === data.templateName) ||
            null;
          if (match) {
            setSelectedTemplate({
              ...match,
              language: match.language?.code || match.language || data.templateLanguage,
              components: data.templateComponents || match.components || []
            });
          } else if (data.templateName) {
            // Fallback: manter seleção local mesmo se Meta não listar o template
            setSelectedTemplate({
              name: data.templateName,
              language: data.templateLanguage,
              components: data.templateComponents || []
            });
          }
        }
      } catch (err) {
        toastError(err);
      } finally {
        if (!cancelled) setLoadingDuplicate(false);
      }
    };

    loadDuplicate();
    return () => {
      cancelled = true;
    };
  }, [open, duplicateFromId]);

  useEffect(() => {
    if (!open) return;
    if (!form.whatsappId) return;
    if (!whatsapps.length) return;
    const selected = Number(form.whatsappId);
    if (!whatsapps.some((w) => Number(w.id) === selected)) {
      setForm((prev) => ({ ...prev, whatsappId: "" }));
    }
  }, [whatsapps, form.whatsappId, open]);

  useEffect(() => {
    if (open && initialContactListProId) {
      setForm((prev) => ({ ...prev, contactListProId: initialContactListProId }));
    }
  }, [open, initialContactListProId]);

  const handleChange = useCallback((field) => (event) => {
    const value = event?.target?.value;
    if (field === "ticketRegister") {
      setForm(prev => ({ ...prev, ticketRegister: event.target.checked }));
    } else {
      setForm(prev => ({ ...prev, [field]: value }));
    }
  }, []);

  const handleSelectTemplate = useCallback((template) => {
    setSelectedTemplate(template);
    setForm(prev => ({
      ...prev,
      templateName: template?.name || "",
      templateLanguage: template?.language || "",
      templateComponentsJson: JSON.stringify(template?.components || [], null, 2)
    }));
  }, []);

  const handleLoadTemplates = useCallback(async (whatsappId) => {
    if (!whatsappId) {
      setTemplates([]);
      return;
    }
    setLoadingTemplates(true);
    try {
      const { data } = await officialCampaignService.templates(whatsappId);
      setTemplates(data?.data || []);
    } catch (err) {
      toastError(err);
    } finally {
      setLoadingTemplates(false);
    }
  }, []);

  const contactListOptions = useMemo(() => [
    ...contactLists.map((list) => ({
      type: "classic",
      id: list.id,
      value: `classic_${list.id}`,
      label: `Lista: ${list.name}`
    })),
    ...contactListsPro.map((list) => ({
      type: "pro",
      id: list.id,
      value: `pro_${list.id}`,
      label: `Lista PRO: ${list.name}${list.contactsCount != null ? ` (${list.contactsCount})` : ""}`
    }))
  ], [contactLists, contactListsPro]);

  const selectedContactListOption = useMemo(() => {
    if (form.contactListProId) {
      return contactListOptions.find((o) => o.type === "pro" && o.id === form.contactListProId) || null;
    }
    if (form.contactListId) {
      return contactListOptions.find((o) => o.type === "classic" && o.id === form.contactListId) || null;
    }
    return null;
  }, [form.contactListId, form.contactListProId, contactListOptions]);

  const estimateContacts = useMemo(() => {
    if (form.contactListProId) {
      const selected = contactListsPro.find(list => list.id === form.contactListProId);
      if (selected?.contactsCount !== undefined) return selected.contactsCount;
      if (selected?.totalImported !== undefined) return selected.totalImported;
      return null;
    }

    if (form.contactListId) {
      const selected = contactLists.find(list => list.id === form.contactListId);
      // Tentar diferentes campos que podem conter o count
      if (selected?.contactsCount !== undefined) return selected.contactsCount;
      if (selected?.total !== undefined) return selected.total;
      if (Array.isArray(selected?.contacts)) return selected.contacts.length;
      // Se não tiver, retornar null para indicar que precisa buscar
      return null;
    }

    if (form.tagIds?.length) {
      const total = form.tagIds.reduce((acc, tagId) => {
        const tag = tags.find(item => item.id === tagId);
        if (!tag) return acc;
        if (Array.isArray(tag.contacts)) {
          return acc + tag.contacts.length;
        }
        if (tag.count !== undefined) return acc + tag.count;
        return acc;
      }, 0);
      return total > 0 ? total : null;
    }

    return null;
  }, [form.contactListId, form.contactListProId, form.tagIds, contactLists, contactListsPro, tags]);

  const estimatedDuration = useMemo(() => {
    if (!estimateContacts || estimateContacts === null) return "—";
    // Usar intervalo padrão de 20 segundos (mesmo das configurações de campanha)
    const intervalSeconds = 20;
    const totalSeconds = estimateContacts * intervalSeconds;
    const minutes = Math.ceil(totalSeconds / 60);
    if (minutes < 1) return "< 1 min";
    return `${minutes} min`;
  }, [estimateContacts]);

  const connectionStatus = useMemo(() => {
    const conn = whatsapps.find(item => item.id === form.whatsappId);
    if (!conn) return null;
    return conn.status;
  }, [whatsapps, form.whatsappId]);

  const resetForm = () => {
    setForm({ ...defaultForm });
    setSelectedTemplate(null);
    setTemplates([]);
  };

  const handleSubmit = async () => {
    try {
      if (!form.whatsappId || !selectedTemplate) {
        toast.error("Selecione uma conexão e um template.");
        return;
      }

      const templateComponents = JSON.parse(form.templateComponentsJson || "[]");

      const payload = {
        name: form.name || selectedTemplate.name,
        contactListId: form.contactListId || null,
        contactListProId: form.contactListProId || null,
        tagIds: form.tagIds,
        whatsappId: form.whatsappId,
        templateName: selectedTemplate.name,
        templateLanguage: selectedTemplate.language,
        templateComponents,
        ticketRegister: form.ticketRegister,
        ticketStatus: form.ticketStatus || "closed",
        ticketQueueId: form.ticketQueueId || null,
        ticketUserId: form.ticketUserId || null,
        scheduledAt: form.scheduledAt || null
      };
      if (form.sendInteractive && form.interactiveBodyText) {
        if (form.interactiveType === "button") {
          const buttons = (form.interactiveButtons || []).filter(b => b && b.title && b.title.trim()).slice(0, 3);
          if (buttons.length) {
            payload.interactivePayload = {
              type: "button",
              bodyText: form.interactiveBodyText,
              buttons: buttons.map((b, i) => ({ id: b.id || String(i + 1), title: String(b.title).substring(0, 20) }))
            };
          }
        } else {
          const rows = (form.interactiveSections?.[0]?.rows || []).filter(r => r && r.title && r.title.trim()).slice(0, 10);
          if (rows.length) {
            payload.interactivePayload = {
              type: "list",
              bodyText: form.interactiveBodyText,
              buttonText: form.interactiveButtonText || "Escolha uma opção",
              sections: [{ title: form.interactiveSections?.[0]?.title || "Opções", rows: rows.map((r, i) => ({ id: r.id || String(i + 1), title: String(r.title).substring(0, 24) })) }]
            };
          }
        }
      }

      setLoading(true);
      const { data } = await officialCampaignService.create(payload);
      await officialCampaignService.schedule(data.id, {
        scheduledAt: form.scheduledAt || null
      });

      toast.success("Campanha oficial agendada com sucesso!");
      resetForm();
      onSuccess?.();
      onClose();
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>
        {duplicateFromId ? "Duplicar campanha oficial" : "Nova campanha oficial"}
      </DialogTitle>
      <DialogContent dividers>
        {(loading || loadingDuplicate) && (
          <Typography variant="body2" color="textSecondary" style={{ marginBottom: 12 }}>
            Carregando dados da campanha...
          </Typography>
        )}
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography className={classes.sectionTitle}>Público-alvo & Conexão</Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Nome da campanha"
              fullWidth
              value={form.name}
              onChange={handleChange("name")}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Autocomplete
              value={selectedContactListOption}
              onChange={(_, option) => {
                if (!option) {
                  setForm((prev) => ({ ...prev, contactListId: "", contactListProId: "" }));
                  return;
                }
                if (option.type === "pro") {
                  setForm((prev) => ({ ...prev, contactListId: "", contactListProId: option.id }));
                } else {
                  setForm((prev) => ({ ...prev, contactListId: option.id, contactListProId: "" }));
                }
              }}
              options={contactListOptions}
              getOptionLabel={(option) => (option && option.label) || ""}
              filterOptions={createFilterOptions({ trim: true })}
              fullWidth
              size="small"
              noOptionsText="Nenhuma lista encontrada"
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Lista de Contatos"
                  variant="outlined"
                  placeholder="Pesquisar lista..."
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel id="tags-label">Tags (múltiplas)</InputLabel>
              <Select
                labelId="tags-label"
                multiple
                value={form.tagIds}
                onChange={handleChange("tagIds")}
                renderValue={selected => (
                  <Box display="flex" flexWrap="wrap" gap={8}>
                    {selected.map(value => {
                      const tag = tags.find(t => t.id === value);
                      return <Chip key={value} label={tag?.name || value} size="small" />;
                    })}
                  </Box>
                )}
              >
                {tags.map(tag => (
                  <MenuItem key={tag.id} value={tag.id}>
                    {tag.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel id="whatsapp-label">Conexão oficial</InputLabel>
              <Select
                labelId="whatsapp-label"
                value={form.whatsappId}
                onChange={(event) => {
                  const value = event.target.value;
                  setForm(prev => ({ ...prev, whatsappId: value }));
                  handleLoadTemplates(value);
                }}
              >
                <MenuItem value="">
                  <em>Selecione</em>
                </MenuItem>
                {whatsapps.map(w => (
                  <MenuItem key={w.id} value={w.id}>
                    {w.name || w.phone_number}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {form.whatsappId && (
              <Box mt={1}>
                <span
                  className={classes.offlineDot}
                  style={{
                    backgroundColor: connectionStatus === "CONNECTED" ? "#4caf50" : "#f44336"
                  }}
                />
                <Typography variant="body2" component="span">
                  {connectionStatus === "CONNECTED" ? "Online" : "Offline"}
                </Typography>
              </Box>
            )}
          </Grid>

          <Grid item xs={12}>
            <Typography className={classes.sectionTitle}>Configurações de Tickets</Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={form.ticketRegister}
                  onChange={handleChange("ticketRegister")}
                  color="primary"
                />
              }
              label="Registrar no sistema"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel id="ticket-status-label">Status do ticket</InputLabel>
              <Select
                labelId="ticket-status-label"
                value={form.ticketStatus}
                onChange={handleChange("ticketStatus")}
              >
                <MenuItem value="closed">Encerrado</MenuItem>
                <MenuItem value="pending">Pendente</MenuItem>
                <MenuItem value="open">Aberto</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel id="queue-label">Fila</InputLabel>
              <Select
                labelId="queue-label"
                value={form.ticketQueueId}
                onChange={handleChange("ticketQueueId")}
              >
                <MenuItem value="">
                  <em>Sem fila</em>
                </MenuItem>
                {queues.map(queue => (
                  <MenuItem key={queue.id} value={queue.id}>
                    {queue.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel id="user-label">Usuário responsável</InputLabel>
              <Select
                labelId="user-label"
                value={form.ticketUserId}
                onChange={handleChange("ticketUserId")}
              >
                <MenuItem value="">
                  <em>Automático</em>
                </MenuItem>
                {users.map(u => (
                  <MenuItem key={u.id} value={u.id}>
                    {u.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              type="datetime-local"
              label="Agendar execução"
              InputLabelProps={{ shrink: true }}
              value={form.scheduledAt}
              onChange={handleChange("scheduledAt")}
            />
          </Grid>

          <Grid item xs={12}>
            <Typography className={classes.sectionTitle}>Template da campanha</Typography>
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel id="template-label">Selecione o template</InputLabel>
              <Select
                labelId="template-label"
                value={selectedTemplate?.name || ""}
                onChange={(event) => {
                  const templateName = event.target.value;
                  const template = templates.find(t => t.name === templateName);
                  if (template) {
                    handleSelectTemplate(template);
                  }
                }}
                disabled={!templates.length || loadingTemplates}
              >
                <MenuItem value="">
                  <em>{loadingTemplates ? "Carregando..." : "Selecione"}</em>
                </MenuItem>
                {templates.map(template => (
                  <MenuItem key={template.id} value={template.name}>
                    {template.name} ({template.language})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Componentes do template (JSON)"
              multiline
              fullWidth
              minRows={6}
              variant="outlined"
              value={form.templateComponentsJson}
              onChange={handleChange("templateComponentsJson")}
              className={classes.templateEditor}
            />
            <Typography variant="caption" color="textSecondary" display="block">
              Use placeholders como {"{{name}}"}, {"{{contact.name}}"}, {"{{var1}}"}, {"{{var2}}"} até {"{{var6}}"} (em listas PRO) para personalizar dinamicamente.
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <Typography className={classes.sectionTitle}>Mensagem com botões/lista (opcional)</Typography>
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={form.sendInteractive || false}
                  onChange={(e) => setForm(prev => ({ ...prev, sendInteractive: e.target.checked }))}
                  color="primary"
                />
              }
              label="Enviar também mensagem com botões ou lista após o template"
            />
          </Grid>
          {form.sendInteractive && (
            <>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Tipo</InputLabel>
                  <Select
                    value={form.interactiveType}
                    onChange={(e) => setForm(prev => ({ ...prev, interactiveType: e.target.value }))}
                    label="Tipo"
                  >
                    <MenuItem value="button">Botões (até 3)</MenuItem>
                    <MenuItem value="list">Lista</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Texto da mensagem"
                  fullWidth
                  size="small"
                  value={form.interactiveBodyText}
                  onChange={(e) => setForm(prev => ({ ...prev, interactiveBodyText: e.target.value }))}
                  placeholder="Ex: Escolha uma opção abaixo"
                />
              </Grid>
              {form.interactiveType === "button" && (
                [1, 2, 3].map((i) => (
                  <Grid item xs={12} md={4} key={i}>
                    <TextField
                      label={`Botão ${i}`}
                      fullWidth
                      size="small"
                      inputProps={{ maxLength: 20 }}
                      value={form.interactiveButtons?.[i - 1]?.title || ""}
                      onChange={(e) => {
                        const next = [...(form.interactiveButtons || defaultForm.interactiveButtons)];
                        next[i - 1] = { ...next[i - 1], id: String(i), title: e.target.value };
                        setForm(prev => ({ ...prev, interactiveButtons: next }));
                      }}
                    />
                  </Grid>
                ))
              )}
              {form.interactiveType === "list" && (
                <>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Texto do botão que abre a lista"
                      fullWidth
                      size="small"
                      value={form.interactiveButtonText}
                      onChange={(e) => setForm(prev => ({ ...prev, interactiveButtonText: e.target.value }))}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="body2" color="textSecondary">Itens da lista (um por linha ou use até 10 itens)</Typography>
                    {(form.interactiveSections?.[0]?.rows || [{ id: "1", title: "" }]).map((row, idx) => (
                      <Box key={idx} display="flex" alignItems="center" gap={1} mt={0.5}>
                        <TextField
                          label={`Item ${idx + 1}`}
                          size="small"
                          fullWidth
                          inputProps={{ maxLength: 24 }}
                          value={row.title || ""}
                          onChange={(e) => {
                            const sec = form.interactiveSections?.[0] || { title: "Opções", rows: [] };
                            const rows = [...(sec.rows || [])];
                            while (rows.length <= idx) rows.push({ id: String(rows.length + 1), title: "" });
                            rows[idx] = { ...rows[idx], id: rows[idx].id || String(idx + 1), title: e.target.value };
                            setForm(prev => ({ ...prev, interactiveSections: [{ ...sec, rows }] }));
                          }}
                        />
                        {(form.interactiveSections?.[0]?.rows || []).length > 1 && (
                          <Button
                            size="small"
                            color="secondary"
                            onClick={() => {
                              const sec = form.interactiveSections?.[0] || { title: "Opções", rows: [] };
                              const rows = (sec.rows || []).filter((_, j) => j !== idx);
                              setForm(prev => ({ ...prev, interactiveSections: [{ ...sec, rows: rows.length ? rows : [{ id: "1", title: "" }] }] }));
                            }}
                          >
                            Remover
                          </Button>
                        )}
                      </Box>
                    ))}
                    <Button
                      size="small"
                      onClick={() => {
                        const sec = form.interactiveSections?.[0] || { title: "Opções", rows: [] };
                        const rows = [...(sec.rows || []), { id: String((sec.rows || []).length + 1), title: "" }];
                        if (rows.length <= 10) setForm(prev => ({ ...prev, interactiveSections: [{ ...sec, rows }] }));
                      }}
                      disabled={(form.interactiveSections?.[0]?.rows || []).length >= 10}
                      style={{ marginTop: 8 }}
                    >
                      + Adicionar item
                    </Button>
                  </Grid>
                </>
              )}
            </>
          )}

          <Grid item xs={12}>
            <Box mt={2} p={2} border={1} borderColor="divider" borderRadius={8}>
              <Typography className={classes.sectionTitle}>Resumo</Typography>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography variant="body2">Total estimado de contatos</Typography>
                <Typography variant="body1" color="primary">
                  {estimateContacts !== null ? `${estimateContacts} contatos` : "—"}
                </Typography>
              </Box>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography variant="body2">Tempo estimado</Typography>
                <Typography variant="body1" color="primary">
                  {estimatedDuration}
                </Typography>
              </Box>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography variant="body2">WhatsApp</Typography>
                <Typography variant="body1" color="primary">
                  {whatsapps.find(w => w.id === form.whatsappId)?.name || "—"}
                </Typography>
              </Box>
              <Typography variant="caption" color="textSecondary">
                A campanha será executada imediatamente após salvar (ou no horário programado).
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="default">
          Cancelar
        </Button>
        <Button color="primary" variant="contained" onClick={handleSubmit} disabled={loading}>
          {loading ? "Salvando..." : "Salvar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default OfficialCampaignModal;

